import {
  Component,
  AfterViewInit,
  ElementRef,
  OnDestroy,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil, throttleTime } from 'rxjs/operators';
import { fromEvent } from 'rxjs';

/**
 * Global back-to-top floating button.
 * Observes scroll of `.main-layout__content` and smoothly scrolls to top.
 * Mounted at MainLayoutComponent level to apply globally to all authenticated pages.
 */
@Component({
  selector: 'app-back-to-top',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './back-to-top.component.html',
  styleUrl: './back-to-top.component.scss',
})
export class BackToTopComponent implements AfterViewInit, OnDestroy {
  private readonly hostEl = inject(ElementRef);
  private readonly destroy$ = new Subject<void>();

  isVisible = signal(false);

  private readonly SCROLL_THRESHOLD = 320; // px
  private scrollContainer: HTMLElement | null = null;

  ngAfterViewInit(): void {
    this.findScrollContainer();
    this.updateVisibility();

    if (!this.scrollContainer) {
      return;
    }

    fromEvent(this.scrollContainer, 'scroll')
      .pipe(throttleTime(100), takeUntil(this.destroy$))
      .subscribe(() => this.updateVisibility());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Finds the layout scroll container.
   */
  private findScrollContainer(): void {
    const host = this.hostEl.nativeElement as HTMLElement;
    this.scrollContainer =
      (host.closest('.main-layout__content') as HTMLElement | null) ??
      (document.querySelector('.main-layout__content') as HTMLElement | null);
  }

  /**
   * Updates visibility based on scroll position.
   */
  private updateVisibility(): void {
    if (!this.scrollContainer) return;

    const scrollTop = this.scrollContainer.scrollTop;
    this.isVisible.set(scrollTop > this.SCROLL_THRESHOLD);
  }

  /**
   * Smoothly scrolls the container to top.
   */
  scrollToTop(): void {
    if (!this.scrollContainer) return;

    this.scrollContainer.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }
}
